import triggerAlert from "./alert.js"
import handleChange from "./change.js"
import { handleLoader, setCurrentFoodImage } from "./loader.js"
import handleProgress from "./progress.js"
import renderOrderData from "./review.js"
import { scrollToTop } from "./scroll.js"
import handleSections from "./sections.js"
import updateName from "./updateName.js"
import syncWeightInputs from './updateWeight'

class QuizWizard extends HTMLElement {

  // Default protein splits
  static FULL_SPLIT = { chicken: 0.35, beef: 0.3, lamb: 0.2, salmon: 0.15 };
  static TRIAL_SPLIT = { chicken: 0.25, beef: 0.25, lamb: 0.25, salmon: 0.25 };

  connectedCallback() {
    console.log('quiz v2', true)
    // State
    this.step = 1;
    // Last step = highest data-step in the form (computed so reordering/adding
    // steps doesn't require touching hard-coded step numbers).
    this.lastStep = Math.max(
      1,
      ...Array.from(this.querySelectorAll('section[data-step]'))
        .map(s => Number(s.dataset.step) || 0)
    );
    this.dogName = 'Marley';
    this.isPuppy = true;
    this.trialCartons = 4;
    this.trialDays = 14;
    this.wpDays = 28;
    this.discountCodeValue = 0;
    this.discountCode = '';
    this.monthlyCartons = 0;
    this.feedingGuide = null;
    this.cartQuantities = {};
    this.trialCartQuantities = {};
    this.halfPlanCartQuantities = {};

    this.orderData = {
      name: { question: "Your dogs name is", answer: "" },
      breed: { question: "Marley's breed is a", answer: "" },
      gender: { question: "Marley is a", answer: "" },
      neutered: { question: "Is Marley Neutered", answer: "" },
      years: { question: "How old is Marley in years", answer: "" },
      months: { question: "How old is Marley in months", answer: "" },
      weight: { question: "Marley weights approximately", answer: "" },
      size: { question: "Marley is", answer: "Normal weight" },
      activity: { question: "Marley is", answer: "Normal activity" },
      allergies: { question: "Marley is allergic to", answer: [] },
      treats: { question: "Marley gets treats", answer: "Sometimes" },
      eating: { question: "Marley is currently eating", answer: [] },
      eating_brand: { question: "Marley's current food brand is", answer: "" },
      hasconditions: { question: "Does Marley have any medical conditions?", answer: "" },
      condition: { question: "Marley's medical conditions", answer: [] },
      thrive: { question: "What we want Marley to thrive at", answer: "" },
      food_issues: { question: "Marley's current food issue", answer: "" },
      other_food_issue: { question: "Other food issue", answer: "" },
      health_issues: { question: "Health issues", answer: [] },
      fussy_dog: { question: "Is Marley a real fussy dog?", answer: "" },
      prefered_treat: { question: "Prefered treat", answer: [] },
      plan: { question: "Marley's plan", answer: "", contents: "" },
      owner_name: { question: "Your name", answer: "" },
      owner_email: { question: "Your email", answer: "" },
      owner_mobile: { question: "Mobile", answer: "" },
    };

    // Button refs
    this.nextButton = this.querySelector('.next__button');
    this.startButton = this.querySelector('.start__button');
    this.checkoutButton = this.querySelector('.checkout__button');
    this.skipButton = this.querySelector('.skip__button');
    this.reviewButton = this.querySelector('.review__button');

    // Show the true question total in the header counter (step 1 is the intro)
    const totalStepsEl = this.querySelector('[data-total-steps]');
    if (totalStepsEl) totalStepsEl.textContent = this.lastStep - 1;

    this._initListeners();
    this._initAgeToggle();
    this._initBreedLogic();
    this._initCheckboxExclusives();
    this._initConditionsToggle();
    this._initThriveCallout();
    this._initAutocomplete();
    this._initResumeFlow();

    window.onbeforeunload = () => true;
    this._checkoutFlow(1);
  }

  disconnectedCallback() {
    window.onbeforeunload = null;
  }

  // ─── Listeners ───────────────────────────────────────────────────────────────

  _initListeners() {
    // Order data tracking
    this.querySelectorAll('[data-order]').forEach(input => {
      input.addEventListener('change', event => {
        handleChange(event, this.orderData);
        this._renderPayloadDebug(); // TESTING ONLY — live payload preview
      });
    });

    // TESTING ONLY — copy the debug payload JSON to the clipboard.
    this.querySelector('[data-quiz-payload-copy]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const json = this.querySelector('[data-quiz-payload-debug] code')?.textContent || '';
      try {
        await navigator.clipboard.writeText(json);
        const original = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = original; }, 1500);
      } catch (err) {
        btn.textContent = 'Copy failed';
      }
    });

    // Navigation buttons
    this.nextButton?.addEventListener('click', () => {
      this._handleSteps();
    });
    this.startButton?.addEventListener('click', () => {
      this._handleSteps();
      this._emptyCartOnStart();
    });
    this.checkoutButton?.addEventListener('click', () => {
      this._handleSteps();
    });
    this.skipButton?.addEventListener('click', () => {
      this._handleSteps();
    });

    // Quiz header back link
    this.querySelector('.quiz-header__back')?.addEventListener('click', () => {
      if (this.step === 1) {
        window.onbeforeunload = null;
        window.location.href = '/';
        return;
      }
      this._navigate('back');
    });

    // Review panel
    this.reviewButton?.addEventListener('click', () => {
      renderOrderData(this.reviewButton, this.orderData);
    });
    this.querySelector('.review__button.close')?.addEventListener('click', () => {
      this.reviewButton.innerHTML = 'Review your choices';
      this.querySelector('.review__button.close').style.display = 'none';
    });

    // Enter key navigation
    document.addEventListener('keydown', this._boundKeydown = (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        document.querySelectorAll('input[type=text], textarea').forEach(el => el.blur());
        if (this.dataset.current == this.lastStep) {
          this.querySelector('.next__button.trigger-post-quiz')?.click();
        } else {
          this.querySelector('.next__button:not(.trigger-post-quiz)')?.click();
        }
      }
    });

    // "Another dog" toggle
    this.querySelector('.wizard__another_dog h3')?.addEventListener('click', (e) => {
      e.preventDefault();
      const info = this.querySelector('.wizard__another_dog div.another_dog_info');
      if (info) info.style.display = info.style.display === 'none' ? 'block' : 'none';
    });

    this.querySelector('.another_dog_info__close')?.addEventListener('click', () => {
      const info = this.querySelector('.wizard__another_dog div.another_dog_info');
      if (info) info.style.display = 'none';
    });

    // Puppy modal listeners (age inputs)
    const yearsInput = this.querySelector('input[data-order="years"]');
    const monthsInput = this.querySelector('input[data-order="months"]');
    const getAgeMonths = () =>
      (Number(yearsInput?.value) * 12) + Number(monthsInput?.value || 0);
    yearsInput?.addEventListener('input', () => this._puppyModalHelper(getAgeMonths()));
    monthsInput?.addEventListener('input', () => this._puppyModalHelper(getAgeMonths()));

    // Gender → neutered/spayed label swap
    this.querySelectorAll('input[type="radio"][name="gender"]').forEach(radio => {
      radio.addEventListener('change', () => {
        const selection = this.querySelector('input[type="radio"][name="gender"]:checked')?.value;
        const label = document.getElementById('neutered-spayed');
        const old = label?.innerHTML || '';
        if (label) {
          label.innerHTML = (selection === 'Boy')
            ? old.replace('Spayed', 'Neutered')
            : old.replace('Neutered', 'Spayed');
        }
        // Document-scoped: gender spans also live on the plan / add-ons pages,
        // which render outside the <quiz-wizard> element.
        document.querySelectorAll('.gender-update').forEach(el => {
          el.textContent = selection === 'Boy' ? 'he' : 'she';
        });
        document.querySelectorAll('.gender-update-possessive').forEach(el => {
          el.textContent = selection === 'Boy' ? 'his' : 'her';
        });
        const neuteredContainer = document.getElementById('neutered-spayed-container');
        if (neuteredContainer) neuteredContainer.style.display = 'block';
      });
    });

    // Limit modal close
    const limitModal = this.querySelector('.limit-modal');
    limitModal?.querySelector('.close')?.addEventListener('click', () => {
      limitModal.style.display = 'none';
    });

    // Email input focus (clear invalid state)
    this.querySelector('.final-step-flow input[name="owner_email"]')?.addEventListener('focus', () => {
      this.querySelector('.invalid-email')?.classList.remove('triggered');
    });

    // Final step → post-wizard transition
    this.querySelector('.trigger-post-quiz')?.addEventListener('click', (e) => {
      if (e.currentTarget.classList.contains('next__button') && this._validateEmail()) {
        this._submitToKlaviyo();

        const gender = this.querySelector('input[name="gender"]:checked')?.value;
        this.querySelectorAll('.dog-gender').forEach(el => { el.innerHTML = gender === 'Boy' ? 'he' : 'she'; });
        this.querySelectorAll('.dog-gender-possessive').forEach(el => { el.innerHTML = gender === 'Boy' ? 'his' : 'her'; });
        // Refresh gender spans on the (soon-to-be-revealed) plan / add-ons pages.
        document.querySelectorAll('.gender-update').forEach(el => { el.textContent = gender === 'Boy' ? 'he' : 'she'; });
        document.querySelectorAll('.gender-update-possessive').forEach(el => { el.textContent = gender === 'Boy' ? 'his' : 'her'; });

        this.dogName = this._qiv('dog_name') || this.dogName;
        updateName(this.dogName);
        this.saveProgress(this.lastStep);

        // The plan calc + AI plan content fetch normally already kicked off
        // when the user reached this step — either via the step-18 "Next"
        // click (_handleSteps) or, on a refresh landing here directly, via
        // _prepopulate — so the AI call has had the whole email step's dwell
        // time to resolve. Fall back to starting both fresh here in case
        // neither ran. _calculateMealPlan is synchronous today, but wrapped
        // so handleLoader's async-safety gate still holds if it ever becomes
        // async — the overlay won't lift onto a half-rendered recommendation.
        // planReady only ever represents the (fast) plan calc, never the AI
        // fetch, so a slow AI response can never hold up the overlay.
        const planReady = this._planReadyPromise || Promise.resolve()
          .then(() => this._calculateMealPlan(JSON.stringify(this._getInput())));
        if (!this._planReadyPromise) planReady.then(() => this._loadPlanContent());

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'checkout', ecommerce: { checkout: { actionField: { step: 17 } } } });

        // Show the "Calculating..." overlay for 3s (or until the plan is ready,
        // whichever is longer), then reveal the recommendation underneath it.
        handleLoader(planReady).then(() => {
          const wizardEl = this.querySelector('.wizard-v2') || this;
          wizardEl.style.display = 'none';
          document.querySelector('.post-wizard-wrapper').style.display = 'block';
          scrollToTop();

          document.querySelector('.wizard-page-post-extra')?.style && (document.querySelector('.wizard-page-post-extra').style.display = 'block');
          document.querySelector('.wizard-section-post-extra')?.style && (document.querySelector('.wizard-section-post-extra').style.display = 'block');

          // Re-initialize accordion tabs to reset event listeners
          document.querySelectorAll('accordion-tab').forEach(accordion => {
            const clone = accordion.cloneNode(true);
            accordion.parentElement.replaceChild(clone, accordion);
          });
        });
      } else {
        this.querySelector('.invalid-email')?.classList.add('triggered');
      }
    });

    // Results-page header back — inverse of the reveal above: hide the plan
    // screen and return to the quiz's final step.
    document.querySelector('.post-wizard-header__back')?.addEventListener('click', () => {
      const wrapper = document.querySelector('.post-wizard-wrapper');
      if (wrapper) wrapper.style.display = '';
      document.querySelectorAll('.wizard-page-post-extra, .wizard-section-post-extra').forEach(el => {
        el.style.display = '';
      });
      const wizardEl = this.querySelector('.wizard-v2') || this;
      wizardEl.style.display = '';
      scrollToTop();
    });

    // Recipe steppers / add-on cards (delegated — both rendered later). Any
    // [data-recipe-step] button resolves its group/key from the nearest
    // ancestor carrying data-recipe-group (a plan row or an add-on card).
    const stepHandler = (e) => {
      const btn = e.target.closest('[data-recipe-step]');
      if (!btn) return;
      const host = btn.closest('[data-recipe-group]');
      if (!host) return;
      this._stepRecipe(host.dataset.recipeGroup, host.dataset.recipeKey, Number(btn.dataset.recipeStep));
    };
    document.querySelector('.psp-box-card__items')?.addEventListener('click', stepHandler);
    document.querySelector('.post-wizard-addons')?.addEventListener('click', stepHandler);

    // Plan box selector (trial/full/half)
    document.querySelectorAll('[data-box-plan-label]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const selectedPlan = e.currentTarget.dataset.boxPlanLabel;
        document.querySelectorAll('[data-selected-plan]').forEach(sp => { sp.innerHTML = selectedPlan; });
        const planInput = document.querySelector(`input[name="box-plan"][value="${selectedPlan}"]`);
        if (planInput) planInput.checked = true;

        if (selectedPlan === 'full plan') {
          document.querySelectorAll('[data-selected-box-price]').forEach(el => {
            el.innerHTML = document.querySelector('[data-full-box-price]')?.innerHTML || '';
          });
          document.querySelectorAll('[data-selected-box-compare-price]').forEach(el => {
            el.innerHTML = document.querySelector('[data-full-box-compare-price]')?.innerHTML || '';
          });
          document.querySelectorAll('[data-quiz-plan-discount="selected"]').forEach(el => {
            el.innerHTML = document.querySelector('[data-quiz-plan-discount="full"]')?.innerHTML || '';
          });
        } else {
          document.querySelectorAll('[data-selected-box-price]').forEach(el => {
            el.innerHTML = document.querySelector('[data-half-box-price]')?.innerHTML || '';
          });
          document.querySelectorAll('[data-selected-box-compare-price]').forEach(el => {
            el.innerHTML = document.querySelector('[data-half-box-compare-price]')?.innerHTML || '';
          });
          document.querySelectorAll('[data-quiz-plan-discount="selected"]').forEach(el => {
            el.innerHTML = document.querySelector('[data-quiz-plan-discount="half"]')?.innerHTML || '';
          });
        }
      });
    });

    // Plan screen "Continue" → advance to the add-ons step (not checkout).
    document.querySelector('[data-goto-addons]')?.addEventListener('click', () => {
      // Guard against advancing while the starter box is below the meal minimum.
      if (this.starterBoxValid === false) return;
      document.querySelector('.post-wizard-wrapper').style.display = 'none';
      document.querySelector('.post-wizard-addons-wrapper').style.display = 'block';
      scrollToTop();
    });

    // Add-ons back → return to the plan overview.
    document.querySelector('[data-addons-back]')?.addEventListener('click', () => {
      document.querySelector('.post-wizard-addons-wrapper').style.display = 'none';
      document.querySelector('.post-wizard-wrapper').style.display = 'block';
      scrollToTop();
    });

    // Add-ons "Continue to checkout" → add items to cart and redirect.
    document.querySelector('[data-checkout-quiz]')?.addEventListener('click', () => {
      const itemsToAdd = Object.entries(this.trialCartQuantities)
        .map(([id, quantity]) => ({ id, quantity }))
        .filter(item => item.quantity > 0);

      const selectedPlan = document.querySelector('input[name="box-plan"]:checked')?.value || 'full plan';

      this._addItemsToCart(itemsToAdd, this.cartQuantities, this.halfPlanCartQuantities, selectedPlan);
    });

    // Quiz resume / restart
    this.querySelector('.continue-quiz')?.addEventListener('click', () => {
      this._emptyCartOnStart();
      this._prepopulate(localStorage.getItem('quizAnswers-v2'));
      setTimeout(() => {
        this.querySelector('.quiz-started-overlay')?.classList.remove('show-modal');
      }, 400);
    });

    this.querySelector('.restart-quiz')?.addEventListener('click', () => {
      localStorage.removeItem('quizAnswers-v2');
      this.querySelector('.quiz-started-overlay')?.classList.remove('show-modal');
    });

    // Weight inputs sync
    syncWeightInputs('weightTextInput', 'weightRangeInput');

    // Discount code entry on the final quiz step
    this.querySelector('#have_discount')?.addEventListener('click', () => {
      this.querySelector('.discount-input-wrapper')?.classList.toggle('active');
    });

    this.querySelector('.apply-coupon')?.addEventListener('click', () => {
      const code = this.querySelector('input[name="discount"]')?.value.trim() || '';
      if (code.length < 3) {
        const statusEl = document.querySelector('.coupon-status');
        statusEl?.classList.remove('coupon-status--active');
        statusEl?.classList.add('coupon-status--error');
        return;
      }
      this._handleCoupon(code);
    });

    // Coupon on load: a cookie code (from a /discount/CODE link) wins over the
    // default code the section prefills into the field; auto-apply either one.
    // If the cookie code is rejected, fall back to the default code so a stale
    // cookie can't silently cost the customer the standing offer.
    const cookieCode = this._getCookie('discount_code');
    const discountInput = this.querySelector('input[name="discount"]');
    const defaultCode = discountInput?.value.trim() || '';
    if (cookieCode && discountInput) discountInput.value = cookieCode;
    const initialCode = cookieCode || defaultCode;
    if (initialCode) this._handleCoupon(initialCode, defaultCode);
  }

  // ─── Navigation ──────────────────────────────────────────────────────────────

  _navigate(click) {
    this.step = (click === 'back') ? this.step - 1 : this.step + 1;

    const counter = document.querySelector('.progress__counter span');
    if (counter) counter.innerHTML = this.step - 1;

    const progressBar = this.querySelector('.quiz-header__progress-bar');
    if (progressBar) progressBar.style.width = `${(this.step / this.lastStep) * 100}%`;

    const headerBack = this.querySelector('.quiz-header__back');
    headerBack?.classList.toggle('disabled', this.step === 1);

    this.dogName = this._qiv('dog_name') || this.dogName;
    updateName(this.dogName);

    handleProgress(click, this.step);
    handleSections(this.step);
    scrollToTop();

    this.dataset.current = this.step;
    if (this.step === 1) this._checkoutFlow(1);
  }

  _handleSteps() {
    const next = this.step + 1;

    if (this.step > 1) this.saveProgress(this.step);

    if (next === 2) {
      if (this._validate(this.step)) {
        this._navigate('next');
        this.nextButton?.classList.remove('disabled');
        this._checkoutFlow(next);
      }
    } else if (next >= 3 && next < this.lastStep) {
      if (this._validate(this.step)) {
        this._navigate('next');
        this._checkoutFlow(next);
      }
    } else if (next === this.lastStep) {
      if (this._validate(this.step)) {
        this._navigate('next');
        this._checkoutFlow(next);
        // Record that the final step was reached so a reload resumes here, not
        // back on the last question. (saveProgress above ran with the prior step.)
        this.saveProgress(this.step);
        // Pick the Trustpilot review that fits the user's answers
        this._updateTrustpilotQuote();

        // Swap the loading-overlay bowl art now, while the user is on the
        // email step, so the image is already fetched by the time the overlay
        // shows. Doing it in handleLoader started the download too late.
        setCurrentFoodImage();

        // Kick off the plan calc + AI plan content fetch as soon as the user
        // lands on the email step (rather than waiting for the final submit)
        // so the AI content has the whole email step's dwell time to resolve.
        // Stashed on `this` so the trigger-post-quiz handler can reuse it.
        this._planReadyPromise = Promise.resolve()
          .then(() => this._calculateMealPlan(JSON.stringify(this._getInput())));
        this._planReadyPromise.then(() => this._loadPlanContent());
      }

      const data = this._getInput();
      console.log('🐕 Quiz completed - User choices:', data);

      const { MER, monthlyCartons } = this._calculateCalories(data);
      this.monthlyCartons = monthlyCartons;

    }

    this.dataset.current = this.step;
  }

  // ─── Validation ──────────────────────────────────────────────────────────────

  _validate(target) {
    let errors = false;
    let customError = false;

    // Reset label styling
    document.querySelectorAll(`section[data-step="${target}"] label`).forEach(label => {
      label.style.color = '#0f4632';
    });

    // Validate required text inputs
    const invalidInputs = document.querySelectorAll(`section[data-step="${target}"] input[required]:invalid`);
    if (invalidInputs.length > 0) {
      invalidInputs.forEach(input => {
        const label = document.querySelector(`label[for="${input.name}"]`);
        if (label) label.style.color = '#ac2227';
      });
      errors = true;
    }

    // Validate radio groups
    document.querySelectorAll(`section[data-step="${target}"] .radio__select`).forEach(group => {
      if (!group.querySelector('input[type="radio"]:checked')) {
        const label = group.previousElementSibling;
        if (label?.tagName === 'LABEL') label.style.color = '#ac2227';
        errors = true;
      }
    });

    // Validate checkbox groups
    document.querySelectorAll(`section[data-step="${target}"] .multiselect__container`).forEach(group => {
      // Skip hidden groups (e.g. the conditions list when "No health conditions" is picked)
      if (group.offsetParent === null) return;

      const checked = group.querySelectorAll('input[type="checkbox"]:checked');
      if (checked.length === 0) {
        const label = group.previousElementSibling;
        if (label?.tagName === 'LABEL') label.style.color = '#ac2227';
        errors = true;
      }
      if (group.dataset.contains === 'allergies' && checked.length === 4) {
        const label = group.previousElementSibling;
        if (label?.tagName === 'LABEL') label.style.color = '#ac2227';
        errors = true;
        customError = `Oh no! Looks like ${this.orderData.name.answer} is allergic to all variants. Please get in touch if you have any questions`;
      }
    });

    document.querySelectorAll(`section[data-step="${target}"] .health__container`).forEach(group => {
      if (!group.querySelector('input[type="checkbox"]:checked')) {
        const label = group.previousElementSibling;
        if (label?.tagName === 'LABEL') label.style.color = '#ac2227';
        errors = true;
      }
    });

    // Weight step — must be at least 1kg (no 0kg dogs)
    if (Number(target) === 7) {
      const weight = parseFloat(document.querySelector('input[name="weight"]')?.value);
      if (isNaN(weight) || weight < 1) {
        errors = true;
        customError = `Please enter ${this.orderData.name.answer || 'your dog'}'s weight (at least 1kg).`;
      }
    }

    if (errors) {
      triggerAlert(customError || undefined);
      return false;
    }
    return true;
  }

  _validateEmail() {
    const emailInput = document.querySelector('input[name="owner_email"]');
    const emailValue = emailInput?.value.trim() || '';
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailValue);
  }

  // ─── Pure Calculation ────────────────────────────────────────────────────────

  _calculateCalories(input) {
    const months = (parseInt(input.years || 0) * 12) + parseInt(input.months || 0);
    const neutered = this._sanitizeBoolean(input.neutered ?? '');

    let multiplier;
    if (months < 6) multiplier = 1.8;
    else if (months < 9) multiplier = 1.6;
    else if (months < 12) multiplier = 1.4;
    else multiplier = 1.2;

    if (months >= 12) {
      if (input.activity < 2) multiplier -= 0.1;
      if (input.activity > 2) multiplier += 0.1;
    }

    if (months < 12) {
      if (input.weightgroup < 2) multiplier *= 1.1;
    } else {
      if (input.weightgroup < 2) multiplier *= 1.1;
      if (input.weightgroup > 2) multiplier *= 0.9;
    }

    if (neutered && months >= 6) multiplier *= 0.9;

    const treatsFactor = 1 - ((input.treats - 1) * 0.05);
    const RER = 70 * Math.pow(input.weight, 0.75);
    const MER = RER * multiplier * treatsFactor;

    const dailyDinings = Math.round(MER / 125);
    const dailyCartons = dailyDinings / 4;
    const monthlyCartons = dailyCartons * this.wpDays;

    return { MER, monthlyCartons, months, neutered };
  }

  _getMealDistribution(split, cartons) {
    const pkg = {
      chicken: split.chicken ? Math.round(cartons * split.chicken) : 0,
      beef: split.beef ? Math.round(cartons * split.beef) : 0,
      lamb: split.lamb ? Math.round(cartons * split.lamb) : 0,
      salmon: split.salmon ? Math.round(cartons * split.salmon) : 0,
    };

    let diff = Math.round(cartons - Object.values(pkg).reduce((a, b) => a + b, 0));
    while (diff > 0) {
      for (const type in pkg) {
        if (diff > 0 && split.hasOwnProperty(type)) {
          pkg[type] += 1;
          diff -= 1;
        }
      }
    }
    return pkg;
  }

  _getFilteredSplit(allowedProteins, isTrial) {
    const base = isTrial
      ? { ...QuizWizard.TRIAL_SPLIT }
      : { ...QuizWizard.FULL_SPLIT };

    for (const key in base) {
      if (!allowedProteins.includes(key)) delete base[key];
    }
    return this._adjustSplitPercentage(base);
  }

  _adjustSplitPercentage(split) {
    const total = Object.values(split).reduce((a, b) => a + b, 0);
    if (total < 1) {
      const diff = 1 - total;
      const count = Object.keys(split).length;
      for (const key in split) split[key] += diff / count;
    }
    return split;
  }

  _getFeedingGuide(mealCount) {
    const pct = mealCount / this.wpDays;
    if (pct < 0.25) return 0.25;
    return Math.round(pct * 4) / 4;
  }

  _sanitizeBoolean(value) {
    const v = String(value).trim().toLowerCase();
    if (v === 'yes') return true;
    if (v === 'no') return false;
    return Boolean(value);
  }

  _getVolumeDiscount(price) {
    if (price >= 12000) return 0.25;
    if (price >= 10000) return 0.2;
    if (price >= 7500) return 0.15;
    return 0.1;
  }

  _getTrialEndDate() {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  // ─── Data Collection ─────────────────────────────────────────────────────────

  _getInput() {
    return {
      name: this._qiv('dog_name'),
      breed: (document.querySelector('form input[name="breed"]:checked')?.value === 'unknown_breed')
        ? 'Pawfect mix'
        : this._qiv('breed__autocomplete'),
      gender: document.querySelector('form input[name="gender"]:checked')?.value,
      neutered: document.querySelector('form input[name="neutered"]:checked')?.value,
      years: parseInt(this._qiv('years')) || 0,
      months: parseInt(this._qiv('months')) || 0,
      weight: parseFloat(this._qiv('weight')),
      weightgroup: parseInt(document.querySelector('input[name="weightgroup"]:checked')?.value),
      activity: parseInt(document.querySelector('input[name="activity"]:checked')?.value),
      treats: parseInt(document.querySelector('input[name="treats"]:checked')?.value),
      allergies: Array.from(document.querySelectorAll('form input[name="allergies"]:checked'))
        .map(el => el.value.toLowerCase()),
      food: Array.from(document.querySelectorAll('form input[name="current__food"]:checked'))
        .map(el => el.value.toLowerCase()),
      food_brand: document.querySelector('form input[name="current_food_brand"]')?.value || '',
      thrive: document.querySelector('form input[name="thrive"]:checked')?.value || '',
      hasconditions: document.querySelector('form input[name="hasconditions"]:checked')?.value || '',
      conditions: Array.from(document.querySelectorAll('form input[name="condition"]:checked'))
        .map(el => el.value),
      health_issues: Array.from(document.querySelectorAll('form input[name="health_issues"]:checked'))
        .map(el => el.value.toLowerCase()),
      food_issues: document.querySelector('form input[name="food_issues"]:checked')?.value || '',
      other_food_issue: document.querySelector('form textarea[name="other_food_issue"]')?.value || '',
      fussy_dog: document.querySelector('form input[name="fussy_dog"]:checked')?.value,
      prefered_treat: Array.from(document.querySelectorAll('form input[name="prefered_treat"]:checked'))
        .map(el => el.value.toLowerCase()),
    };
  }

  _qiv(name) {
    const el = document.querySelector(`form input[name="${name}"]`);
    return (el != null) ? el.value : false;
  }

  // ─── View Updates ────────────────────────────────────────────────────────────

  _setHTML(selector, value) {
    document.querySelectorAll(selector).forEach(el => { el.innerHTML = value; });
  }

  _show(selector) {
    document.querySelectorAll(selector).forEach(el => { el.style.display = ''; });
  }

  _hide(selector) {
    document.querySelectorAll(selector).forEach(el => { el.style.display = 'none'; });
  }

  _renderProductCards(cartObj, trialObj, halfObj) {
    document.querySelectorAll('.wizard-product-card').forEach(card => {
      const variantId = card.getAttribute('data-variant-id');

      if ((cartObj[variantId] && cartObj[variantId] !== 0) ||
        (trialObj[variantId] && trialObj[variantId] !== 0)) {
        card.classList.add('is-selected');

        const halfPlanBox = card.closest('.plan-box--half .plan-box__summary-cards');
        if (halfPlanBox) {
          const mealsDetail = card.closest('[data-plan-type="meals"]');
          const span = card.querySelector('span');
          if (span) {
            span.innerHTML = (mealsDetail && halfObj[variantId])
              ? halfObj[variantId]
              : cartObj[variantId];
          }
        }

        const fullPlanBox = card.closest('.plan-box--full .plan-box__summary-cards');
        if (fullPlanBox) {
          const span = card.querySelector('span');
          if (span) span.innerHTML = cartObj[variantId];
        }
      }

      const priceWrapper = card.querySelector('.wizard-product-card__price-wrapper');
      if (priceWrapper) {
        const price = card.dataset.price;
        const discounted = priceWrapper.querySelector('.wizard-product-card__discounted-price');
        if (discounted) {
          discounted.innerHTML = Shopify.formatMoney(
            price * (1 - this.discountCodeValue),
            Shopify.money_format
          );
        }
      }
    });
  }

  // Show only the flavour bowls whose protein is present (count > 0) in the
  // given package; hide the rest so the imagery matches the recommendation.
  _renderFlavorBowls(foodPackage) {
    document.querySelectorAll('[data-flavor-bowl]').forEach(bowl => {
      const flavor = bowl.dataset.flavorBowl;
      const included = (foodPackage[flavor] || 0) > 0;
      bowl.style.display = included ? '' : 'none';
    });
  }

  // ─── Meal Plan Calculation ────────────────────────────────────────────────────

  _calculateMealPlan(payload) {
    const payloadData = JSON.parse(payload);
    let fullPrice = 0;
    let trialPrice = 0;
    let fullPlanPrice = 0;

    const subscriptionData = {
      dog_name: payloadData.name,
      dog_breed: payloadData.breed,
      dog_gender: payloadData.gender,
      dog_neutered: this._sanitizeBoolean(payloadData.neutered ?? ''),
      dog_years: payloadData.years,
      dog_months: payloadData.months,
      dog_weight: payloadData.weight,
      dog_weightgroup: payloadData.weightgroup,
      dog_activity: payloadData.activity,
      dog_treats: payloadData.treats,
      dog_allergies: payloadData.allergies,
      dog_eating: payloadData.food,
      dog_fussy_dog: payloadData.fussy_dog,
      dog_prefered_treat: payloadData.prefered_treat,
      dog_health_issues: payloadData.health_issues,
    };

    // Build protein splits, removing allergies
    let split = { ...QuizWizard.FULL_SPLIT };
    let trialSplit = { ...QuizWizard.TRIAL_SPLIT };
    subscriptionData.dog_allergies.forEach(allergy => {
      delete split[allergy.toLowerCase()];
      delete trialSplit[allergy.toLowerCase()];
    });
    split = this._adjustSplitPercentage(split);
    trialSplit = this._adjustSplitPercentage(trialSplit);

    const { MER, monthlyCartons, months } = this._calculateCalories(payloadData);
    this.monthlyCartons = monthlyCartons;

    if (months > 11) this.isPuppy = false;

    const foodPackage = this._getMealDistribution(split, monthlyCartons);
    const trialFoodPackage = this._getMealDistribution(trialSplit, this.trialCartons);

    // Treats
    let preferedTreat = {};
    if (subscriptionData.dog_treats > 1) {
      let treatList = ['lamb', 'chicken', 'beef'];
      if (subscriptionData.dog_prefered_treat == 'none' ||
        (Array.isArray(subscriptionData.dog_prefered_treat) && subscriptionData.dog_prefered_treat.includes('none'))) {
        subscriptionData.dog_allergies.forEach(allergy => {
          const idx = treatList.indexOf(allergy.toLowerCase());
          if (idx !== -1) treatList.splice(idx, 1);
        });
        if (treatList.length) {
          preferedTreat[treatList[0]] = 1;
        } else {
          preferedTreat = 'no treats';
        }
      } else {
        if (Array.isArray(subscriptionData.dog_prefered_treat)) {
          subscriptionData.dog_prefered_treat.forEach(item => { preferedTreat[item] = 1; });
        } else if (subscriptionData.dog_prefered_treat) {
          preferedTreat[subscriptionData.dog_prefered_treat] = 1;
        }
      }
    }

    // Supplements
    let supplements = {};
    if (months >= 6) {
      const issues = subscriptionData.dog_health_issues;
      if (issues.includes('dry, itchy or dull coat')) supplements.oil = 1;
      if (issues.includes('a sensitive stomach')) supplements.gut = 1;
      if (issues.includes('dental issues or bad breath')) supplements.dental = 1;
      if (issues.includes('joint stiffness or pain')) supplements.joint = 1;
      if (months >= 84 && !supplements.joint) supplements.joint = 1;
    }

    const totalPackage = { foodPackage, preferedTreat, supplements };
    const trialPackage = { foodPackage: trialFoodPackage, preferedTreat, supplements };

    const productList = window.productList || [];

    // Meal-only carts here; treats/supplements are added by _repriceStarterBox.
    const cartQuantities = this.cartQuantities = {};
    const trialCartQuantities = this.trialCartQuantities = {};

    // Meals
    for (const product of productList) {
      for (const [key, value] of Object.entries(totalPackage.foodPackage)) {
        if (product.title.toLowerCase().includes(key)) {
          cartQuantities[product.variants[0].id] = value;
          fullPrice += product.price * value;
          break;
        }
      }
      for (const [key, value] of Object.entries(trialPackage.foodPackage)) {
        if (product.title.toLowerCase().includes(key)) {
          trialCartQuantities[product.variants[0].id] = value;
          trialPrice += product.price * value;
          break;
        }
      }
    }
    fullPlanPrice += fullPrice;

    const mealTotalCount = Object.values(totalPackage.foodPackage).reduce((s, v) => s + v, 0);
    this.feedingGuide = this._getFeedingGuide(mealTotalCount);

    const halfPlanMeals = Math.ceil(mealTotalCount / 2);
    const allowedProteins = Object.keys(totalPackage.foodPackage).filter(k => totalPackage.foodPackage[k] > 0);
    const halfPlanSplit = this._getFilteredSplit(allowedProteins, false);
    const halfPlanDistribution = this._getMealDistribution(halfPlanSplit, halfPlanMeals);

    const halfPlanCartQuantities = this.halfPlanCartQuantities = {};
    let halfPlanMealPrice = 0;
    for (const product of productList) {
      for (const [key, value] of Object.entries(halfPlanDistribution)) {
        if (product.title.toLowerCase().includes(key)) {
          halfPlanCartQuantities[product.variants[0].id] = value;
          halfPlanMealPrice += product.price * value;
          break;
        }
      }
    }

    // Half-plan meal count only — starter-box summary is owned by the editor.
    this._setHTML('.meal-portions', halfPlanMeals);

    // Meals are fixed by the quiz; treats/supplements are editable and priced
    // by _repriceStarterBox. Store the meal-only bases (price + subscription
    // carts) so reprice can add the current add-on quantities on top.
    this.fullPlanMealPrice = fullPlanPrice;   // meals only at this point
    this.halfPlanMealPrice = halfPlanMealPrice;
    this.fullPlanMealCart = { ...cartQuantities };        // meal variants only
    this.halfPlanMealCart = { ...halfPlanCartQuantities }; // meal variants only

    // Legacy (old-design) trial spans + coupon %. All PSP plan/starter prices
    // and subscription carts are owned by _repriceStarterBox.
    const fmt = (val) => Shopify.formatMoney(val, Shopify.money_format);
    this._setHTML('.pw-trial.post-wizard-recommendation__info-discount--full-price', fmt(trialPrice));
    this._setHTML('.pw-trial.post-wizard-recommendation__info-discount--discounted-price', fmt(trialPrice * (1 - this.discountCodeValue)));
    this._setHTML('.pw-trial.post-wizard-recommendation__info-discount--discounted-percentage', this.discountCodeValue * 100);
    this._setHTML('[data-quiz-trial-discount]', this.discountCodeValue * 100);

    this._setHTML('[data-trial-end-date]', this._getTrialEndDate());

    updateName(payloadData.name);

    this._initStarterBoxEditor(trialFoodPackage, preferedTreat, supplements, months);
  }

  _renderPriorities(priorities) {
    const list = document.querySelector('[data-plan-priorities]');
    if (!list || !priorities?.length) return;

    const checkSvgTemplate = document.createElement('template');
    checkSvgTemplate.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 18C10.1821 18.0015 11.3529 17.7694 12.445 17.317C13.5372 16.8646 14.5291 16.2009 15.3639 15.3639C16.2009 14.5291 16.8646 13.5372 17.317 12.445C17.7694 11.3529 18.0015 10.1821 18 9C18.0015 7.81789 17.7694 6.64712 17.317 5.555C16.8646 4.46287 16.2009 3.4709 15.3639 2.63611C14.5291 1.79912 13.5372 1.13536 12.445 0.682965C11.3529 0.230575 10.1821 -0.00152412 9 7.53144e-06C7.81789 -0.00149947 6.64712 0.230611 5.555 0.683C4.46287 1.13539 3.4709 1.79914 2.63611 2.63611C1.79914 3.4709 1.13539 4.46287 0.683 5.555C0.230611 6.64712 -0.00149947 7.81789 7.53144e-06 9C-0.00152412 10.1821 0.230575 11.3529 0.682965 12.445C1.13536 13.5372 1.79912 14.5291 2.63611 15.3639C3.4709 16.2009 4.46287 16.8646 5.555 17.317C6.64712 17.7694 7.81789 18.0015 9 18Z" fill="#045A2F"/><path d="M8.50402 13.7488C8.26835 13.7488 8.04838 13.6238 7.93127 13.42L5.36604 8.9502C5.1875 8.63966 5.30034 8.24552 5.616 8.06988C5.92736 7.89353 6.33157 8.00242 6.51154 8.31508L8.42976 11.6572L11.4592 4.83873C11.6063 4.51204 11.9977 4.36239 12.3248 4.50571C12.6575 4.64904 12.8089 5.02984 12.6647 5.35724L9.10677 13.3617C9.00679 13.5858 8.78684 13.7355 8.53689 13.7481C8.52689 13.7488 8.51547 13.7488 8.50404 13.7488H8.50402Z" fill="#DFF3D6"/></svg>`;

    list.replaceChildren(...priorities.map(p => {
      const li = document.createElement('li');
      li.appendChild(checkSvgTemplate.content.cloneNode(true));
      const span = document.createElement('span');
      span.textContent = p; // AI-provided text — textContent only, never HTML-parsed
      li.appendChild(span);
      return li;
    }));
  }

  // ─── Starter Box Editor ──────────────────────────────────────────────────────

  // Recipe display names, keyed by the same protein/issue keys the plan uses.
  static RECIPE_LABELS = {
    meals: { chicken: 'Chicken', beef: 'Beef', lamb: 'Lamb', salmon: 'Salmon' },
    treats: { chicken: 'Chicken', beef: 'Beef', lamb: 'Lamb' },
    supplements: { oil: 'Omega Oil', gut: 'Gut Health', dental: 'Dental Health', joint: 'Joint Health' },
  };

  // Build a { key: {variantId, price, title, image} } index by matching each
  // recipe key against the product titles in the given Shopify product list.
  // title/image are used by the add-ons cards; variantId/price by pricing.
  _indexProducts(list, keys) {
    const index = {};
    for (const key of keys) {
      const product = (list || []).find(p => p.title.toLowerCase().includes(key));
      if (product) {
        index[key] = {
          variantId: product.variants[0].id,
          price: product.price,
          title: product.title,
          image: product.featured_image || (product.images && product.images[0]) || '',
        };
      }
    }
    return index;
  }

  _initStarterBoxEditor(trialFoodPackage, preferedTreat, supplements, months) {
    const labels = QuizWizard.RECIPE_LABELS;

    // Editable quantity maps (packs of each recipe in the starter box).
    const treatMap = (typeof preferedTreat === 'object') ? { ...preferedTreat } : {};
    const suppMap = (months >= 6) ? { ...supplements } : {};

    this.starterBox = {
      meals: { ...trialFoodPackage },
      treats: treatMap,
      supplements: suppMap,
    };

    // Snapshot which add-ons the quiz originally recommended (qty > 0 at init).
    // This fixes each add-on card's column on the add-ons step — editing the
    // quantity later changes the card's state, never which side it sits on.
    this.recommendedAddons = {
      treats: new Set(Object.keys(treatMap).filter(k => treatMap[k] > 0)),
      supplements: new Set(Object.keys(suppMap).filter(k => suppMap[k] > 0)),
    };

    // Priced product index per group, covering every offered recipe so the
    // user can add ones not initially recommended (shown as "0 pack").
    this.starterIndex = {
      meals: this._indexProducts(window.productList, Object.keys(labels.meals)),
      treats: this._indexProducts(window.treatsList, Object.keys(labels.treats)),
      supplements: this._indexProducts(window.supplementList, Object.keys(labels.supplements)),
    };
    // Dogs under 6 months aren't recommended supplements, but the customer can
    // still add them from the add-ons step — reprice reveals the row when any
    // supplement has a quantity. Store eligibility for that logic.
    this.supplementsEligible = months >= 6;

    this._renderRecipeEditors();
    this._repriceStarterBox();
  }

  // Render stepper rows into each [data-recipe-edits] container. Every recipe
  // the index knows about gets a row, defaulting to 0 packs when not present.
  _renderRecipeEditors() {
    const labels = QuizWizard.RECIPE_LABELS;

    for (const group of ['meals', 'treats', 'supplements']) {
      const container = document.querySelector(`[data-recipe-edits="${group}"]`);
      if (!container) continue;

      const rows = Object.keys(this.starterIndex[group]).map(key => {
        const qty = this.starterBox[group][key] || 0;
        return `
          <div class="psp-recipe-edit" data-recipe-group="${group}" data-recipe-key="${key}">
            <div class="psp-recipe-edit__label">
              <b>${labels[group][key]}</b>
              <span><span data-recipe-qty>${qty}</span> pack</span>
            </div>
            <div class="psp-stepper">
              <button type="button" class="psp-stepper__btn" data-recipe-step="-1" aria-label="Remove one">&ndash;</button>
              <span class="psp-stepper__value" data-recipe-value>${qty}</span>
              <button type="button" class="psp-stepper__btn" data-recipe-step="1" aria-label="Add one">+</button>
            </div>
          </div>`;
      }).join('');

      container.innerHTML = rows;
    }
  }

  // ─── Add-ons Step ─────────────────────────────────────────────────────────────

  // Placeholder marketing copy per add-on (will be product/AI data later).
  static ADDON_COPY = {
    treats: {
      chicken: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: 'Stat placeholder' },
      beef: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: 'Stat placeholder' },
      lamb: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: 'Stat placeholder' },
    },
    supplements: {
      gut: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: '71% report better digestion' },
      dental: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: 'Stat placeholder' },
      joint: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: 'Stat placeholder' },
      oil: { desc: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.', stat: 'Stat placeholder' },
    },
  };

  // Render the add-ons page from the current starter box. Add-ons already in
  // the box (qty > 0) go in the "Picked for" column; the rest in the upsell
  // grid. Called by _repriceStarterBox so edits on either page stay in sync.
  _renderAddons() {
    const pickedGrid = document.querySelector('[data-addons-picked-grid]');
    const upsellGrid = document.querySelector('[data-addons-upsell-grid]');
    if (!pickedGrid || !upsellGrid) return;

    const picked = [];
    const upsell = [];

    for (const group of ['treats', 'supplements']) {
      for (const [key, priced] of Object.entries(this.starterIndex[group])) {
        const qty = this.starterBox[group][key] || 0;
        const card = this._addonCard(group, key, priced, qty);
        // Column is fixed by the original recommendation, not current qty, so a
        // card stays put when its quantity changes — only its state updates.
        const wasRecommended = this.recommendedAddons?.[group]?.has(key);
        (wasRecommended ? picked : upsell).push(card);
      }
    }

    pickedGrid.innerHTML = picked.join('');
    upsellGrid.innerHTML = upsell.join('');

    // State 3: nothing in the box → hide the "Picked for" column and drop the
    // "You Might Also Like" heading to a plain centred grid.
    const pickedCol = document.querySelector('[data-addons-picked]');
    if (pickedCol) pickedCol.hidden = picked.length === 0;
    document.querySelector('[data-addons-layout]')?.classList.toggle('post-wizard-addons__container--upsell-only', picked.length === 0);
  }

  _addonCard(group, key, priced, qty) {
    const fmt = (val) => Shopify.formatMoney(val, Shopify.money_format);
    const inBox = qty > 0;

    const copy = QuizWizard.ADDON_COPY[group]?.[key] || {};
    const desc = copy.desc || '';
    const stat = copy.stat || '';

    // Compare price: strike the list price, show the discounted per-pack.
    const discounted = priced.price * (1 - this.discountCodeValue);
    const comparePrice = this.discountCodeValue > 0
      ? `<s>${fmt(priced.price)}</s> ${fmt(discounted)}/each`
      : `${fmt(priced.price)}/each`;

    const image = priced.image
      ? `<img src="${priced.image}" alt="${priced.title}" loading="lazy">`
      : '';

    const badge = inBox
      ? `<div class="post-wizard-addon-card__badge">${this._checkSvgSmall()}<span>In your box</span></div>`
      : '';

    const control = inBox
      ? `<div class="psp-stepper post-wizard-addon-card__stepper">
           <button type="button" class="psp-stepper__btn" data-recipe-step="-1" aria-label="Remove one">&ndash;</button>
           <span class="psp-stepper__value" data-recipe-value>${qty}</span>
           <button type="button" class="psp-stepper__btn" data-recipe-step="1" aria-label="Add one">+</button>
         </div>`
      : `<button type="button" class="post-wizard-addon-card__add" data-recipe-step="1">+ Add</button>`;

    return `
      <div class="post-wizard-addon-card${inBox ? ' post-wizard-addon-card--in-box' : ''}" data-recipe-group="${group}" data-recipe-key="${key}">
        <div class="post-wizard-addon-card__image">
          ${image}
          ${badge}
        </div>
        <div class="post-wizard-addon-card__info">
          <p class="post-wizard-addon-card__name">${priced.title}</p>
          ${desc ? `<p class="post-wizard-addon-card__desc" style="display: none;">${desc}</p>` : ''}
          ${stat ? `<p class="post-wizard-addon-card__stat" style="display: none;">${stat}</p>` : ''}
          <p class="post-wizard-addon-card__price">${comparePrice}</p>
          ${control}
        </div>
      </div>`;
  }

  _checkSvgSmall() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="7" viewBox="0 0 8 7" fill="none"><path d="M1 3.5 3 5.5 7 1" stroke="#E6FFD4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  // Recompute starter-box price, cart quantities, and all summary displays
  // from the current editable maps. Single source of truth after any edit.
  _repriceStarterBox() {
    const fmt = (val) => Shopify.formatMoney(val, Shopify.money_format);
    const trialCartQuantities = {};
    let trialPrice = 0;

    const groupTotals = { meals: 0, treats: 0, supplements: 0 };

    for (const group of ['meals', 'treats', 'supplements']) {
      for (const [key, priced] of Object.entries(this.starterIndex[group])) {
        const qty = this.starterBox[group][key] || 0;
        if (qty > 0) {
          trialCartQuantities[priced.variantId] = qty;
          trialPrice += priced.price * qty;
          groupTotals[group] += priced.price * qty;
        }
      }
    }

    this.trialCartQuantities = trialCartQuantities;

    // Summary counts
    const mealCount = Object.values(this.starterBox.meals).reduce((s, v) => s + v, 0);
    const treatCount = Object.values(this.starterBox.treats).reduce((s, v) => s + v, 0);
    const suppCount = Object.values(this.starterBox.supplements).reduce((s, v) => s + v, 0);

    this._setHTML('.meal-portions-full', mealCount);
    this._setHTML('.treat-count', treatCount);
    this._setHTML('.supplements-quantity', suppCount);

    const mealNames = Object.keys(this.starterBox.meals)
      .filter(k => this.starterBox.meals[k] > 0)
      .map(k => QuizWizard.RECIPE_LABELS.meals[k])
      .join(', ');
    this._setHTML('.meal-contains', mealNames);

    // Per-group /day prices, rounded to whole pence per line. The footer
    // compare/day is the SUM of these rounded lines, so the displayed line
    // prices always add up to the total the customer sees (avoids a ±1p
    // discrepancy from rounding each figure off the raw pence independently).
    const perDayPence = (total) => Math.round(total / this.trialDays);
    const mealDayPence = perDayPence(groupTotals.meals);
    const suppDayPence = perDayPence(groupTotals.supplements);
    const treatDayPence = perDayPence(groupTotals.treats);
    const comparePerDayPence = mealDayPence + suppDayPence + treatDayPence;

    const fmtDay = (pence) => fmt(pence) + '/day';
    this._setHTML('[data-meal-day-price]', fmtDay(mealDayPence));
    this._setHTML('[data-supplement-day-price]', fmtDay(suppDayPence));
    this._setHTML('[data-treat-day-price]', fmtDay(treatDayPence));

    // Meal & treat rows stay visible even at 0 packs. The supplements row is
    // shown for eligible dogs, and — once a non-eligible dog has added a
    // supplement — stays visible even after they remove it, so they can re-add.
    // It only stays hidden for a non-eligible dog who has never added any.
    if (suppCount > 0) this.supplementsTouched = true;
    if (this.supplementsEligible || this.supplementsTouched) {
      this._show('[data-plan-type="supplements"]');
    } else {
      this._hide('[data-plan-type="supplements"]');
    }

    // Bowls follow the current meal selection.
    this._renderFlavorBowls(this.starterBox.meals);

    // Footer + sticky toolbar prices (compare = full, price = discounted).
    const discounted = trialPrice * (1 - this.discountCodeValue);
    const discountedPerDayPence = Math.round(comparePerDayPence * (1 - this.discountCodeValue));
    this._setHTML('[data-trial-box-compare-price]', fmt(trialPrice));
    this._setHTML('[data-trial-box-price]', fmt(discounted));
    this._setHTML('[data-trial-box-compare-price-day]', fmtDay(comparePerDayPence));
    this._setHTML('[data-trial-box-price-day]', fmtDay(discountedPerDayPence));
    this._setHTML('[data-trial-box-savings]', `-${fmt(trialPrice - discounted)}`);

    // ── Full / Half subscription plans ──
    // Displayed plan prices are MEALS ONLY — treats and supplements are priced
    // separately in their own rows and must not inflate the /day figures (or
    // the volume-discount tier) shown on the plan cards. The carts below still
    // carry the add-on variants so checkout is unaffected.
    const fullPlanPrice = this.fullPlanMealPrice || 0;
    const halfPlanPrice = this.halfPlanMealPrice || 0;
    const fullPlanDiscount = this._getVolumeDiscount(fullPlanPrice);
    const halfVolumeDiscount = this._getVolumeDiscount(halfPlanPrice);

    // Subscription carts = meal base + editable add-ons (same variants).
    const fullPlanCart = { ...(this.fullPlanMealCart || {}) };
    const halfPlanCart = { ...(this.halfPlanMealCart || {}) };
    for (const group of ['treats', 'supplements']) {
      for (const [key, priced] of Object.entries(this.starterIndex[group])) {
        const qty = this.starterBox[group][key] || 0;
        if (qty > 0) {
          fullPlanCart[priced.variantId] = qty;
          halfPlanCart[priced.variantId] = qty;
        }
      }
    }
    this.cartQuantities = fullPlanCart;
    this.halfPlanCartQuantities = halfPlanCart;

    // Full plan (per-day over the cycle) + its "selected" mirror.
    const fullPerDay = fmt(fullPlanPrice / this.wpDays);
    const fullDiscPerDay = fmt((fullPlanPrice * (1 - fullPlanDiscount)) / this.wpDays);
    this._setHTML('[data-full-box-compare-price]', fullPerDay);
    this._setHTML('[data-full-box-price]', fullDiscPerDay + '/day');
    this._setHTML('[data-selected-box-compare-price]', fullPerDay);
    this._setHTML('[data-selected-box-price]', fullDiscPerDay + '/day');
    this._setHTML('[data-quiz-plan-discount="full"], [data-quiz-plan-discount="selected"]', fullPlanDiscount * 100);

    // Full plan legacy spans (old-design PSP summary).
    this._setHTML('.pw-full.post-wizard-recommendation__info-discount--full-price', fmt(fullPlanPrice));
    this._setHTML('.pw-full.post-wizard-recommendation__info-discount--discounted-price', fmt(fullPlanPrice * (1 - fullPlanDiscount)));
    this._setHTML('.pw-full.post-wizard-recommendation__info-discount--discounted-percentage', fullPlanDiscount * 100);

    // Half plan.
    this._setHTML('[data-half-box-compare-price]', fmt(halfPlanPrice / this.wpDays));
    this._setHTML('[data-half-box-price]', fmt((halfPlanPrice * (1 - halfVolumeDiscount)) / this.wpDays) + '/day');
    this._setHTML('[data-quiz-plan-discount="half"]', halfVolumeDiscount * 100);

    // Starter box must keep at least trialCartons (4) meals to check out.
    this._validateStarterBox(mealCount);

    // Refresh the plan-step dropdown steppers and the add-ons cards so
    // treat/supplement edits stay in sync across both pages.
    this._renderRecipeEditors();
    this._renderAddons();

    this._logCartState();
  }

  // Debug: log the cart payload that would be sent at checkout, reflecting the
  // current product/add-on selection. Fires on every reprice (any change).
  _logCartState() {
    const selectedPlan = document.querySelector('input[name="box-plan"]:checked')?.value || 'full plan';
    const selectedPlanCart = selectedPlan === 'half plan'
      ? this.halfPlanCartQuantities
      : this.cartQuantities;

    // First box = line items actually added to cart (trial quantities).
    const firstBoxItems = Object.entries(this.trialCartQuantities)
      .filter(([, qty]) => qty > 0)
      .map(([id, quantity]) => ({ id: parseInt(id), quantity }));

    console.log('🛒 [wizard-v2] cart state', {
      selectedPlan,
      firstBoxItems,
      attributes: {
        _selected_plan: selectedPlan,
        _full_month_plan: selectedPlanCart,
        _dog_name: this.dogName,
      },
    });
  }

  // Block checkout when the edited starter box drops below the minimum meals.
  _validateStarterBox(mealCount) {
    const valid = mealCount >= this.trialCartons;
    const button = document.querySelector('.psp-toolbar__continue');
    const error = document.querySelector('[data-starter-box-error]');

    if (button) {
      button.disabled = !valid;
      button.classList.toggle('psp-toolbar__continue--disabled', !valid);
    }
    if (error) error.hidden = valid;

    this.starterBoxValid = valid;
  }

  // Apply a +/- step to a recipe and refresh its row + all prices.
  _stepRecipe(group, key, delta) {
    if (!this.starterBox?.[group]) return;
    const current = this.starterBox[group][key] || 0;
    const next = Math.max(0, current + delta);
    if (next === current) return;
    this.starterBox[group][key] = next;

    // _repriceStarterBox re-renders both the plan-step steppers and the add-on
    // cards, so the clicked control updates without a targeted DOM tweak here.
    this._repriceStarterBox();
  }

  // ─── API ─────────────────────────────────────────────────────────────────────

  // Validate + apply a discount code (from the final-step entry field, the
  // discount_code cookie, or saved progress).
  _handleCoupon(coupon, fallbackCode = '') {
    if (!coupon || coupon.length < 3) return;

    const store = Shopify.shop;
    fetch(`https://netlify.blacklemon.dk/.netlify/functions/checkCouponCode?store=${store}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `ccode=${encodeURIComponent(JSON.stringify(coupon))}`,
    })
      .then(r => r.json())
      .then(response => {
        const statusEl = document.querySelector('.coupon-status');
        if (response.error) {
          // Rejected cookie/saved code — retry once with the default code.
          if (fallbackCode && fallbackCode !== coupon) {
            const input = document.querySelector('input[name="discount"]');
            if (input) input.value = fallbackCode;
            this._handleCoupon(fallbackCode);
            return;
          }
          statusEl?.classList.remove('coupon-status--active');
          statusEl?.classList.add('coupon-status--error');
          this._hide('.post-wizard-recommendation__info-discount-wrapper');
          this.discountCode = '';
        } else {
          this.discountCodeValue = response.success / 100;
          statusEl?.classList.add('coupon-status--active');
          statusEl?.classList.remove('coupon-status--error');
          const msg = document.querySelector('.coupon-status_message--valid span');
          if (msg) msg.innerHTML = response.success;
          document.querySelectorAll('.package-original-price').forEach(el => {
            el.classList.add('package-original-price--crossed');
          });
          this._show('.post-wizard-recommendation__info-discount-wrapper');
          this.discountCode = coupon;

          // If the plan was already priced before this response arrived (now
          // possible as early as the step-18 "Next" click, well before the
          // plan screen is even shown), the discount fields were written
          // with the stale 0 — re-run the calculation so the pill and prices
          // reflect the applied code. Check starterBox (set by
          // _calculateMealPlan) rather than the plan wrapper's visibility,
          // since pricing can now happen long before that wrapper is shown.
          if (this.starterBox) {
            this._calculateMealPlan(JSON.stringify(this._getInput()));
          }
        }
      })
      .catch(() => triggerAlert('An error occurred'));
  }

  async _addItemsToCart(items, fullPlanCart, halfPlanCart, selectedPlan) {
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(item => ({
            id: parseInt(item.id),
            quantity: item.quantity,
            selling_plan: '8019935518',
            properties: {
              _dog_name: this.dogName,
              _dynamicBoxIndex: 0,
              _dynamicBoxId: 'b47674e0-72a2-4bff-9c7b-4f2cc0ef47d9',
            },
          })),
        }),
      }).then(r => r.json());

      const selectedPlanCart = selectedPlan === 'half plan' ? halfPlanCart : fullPlanCart;

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            _hidden_note: JSON.stringify(this._getInput()),
            _quiz_version: 'wizard-v2',
            _feeding_guide: this.feedingGuide,
            _dog_name: this.dogName,
            _selected_plan: selectedPlan,
            _full_month_plan: JSON.stringify(selectedPlanCart),
          },
        }),
      }).then(r => r.json());

      // Clear the leave-page guard so the checkout redirect doesn't prompt.
      window.onbeforeunload = null;
      window.location.href = this.discountCode.length
        ? `/checkout?discount=${this.discountCode}`
        : '/checkout';
    } catch (err) {
      console.error('Error adding items to cart:', err);
    }
  }

  _submitToKlaviyo() {
    const newsletter = document.querySelector('.final-step-flow input[name="mc_signup"]');
    if (!newsletter?.checked) return;

    const userEmail = document.querySelector('.final-step-flow [name="owner_email"]')?.value;
    const profile = this._getInput();
    if (!profile) return;

    const activityMap = { 1: 'Pretty snoozy', 2: 'Active', 3: 'Explosion of energy' };
    const bodyTypeMap = { 1: 'A bit skinny', 2: 'Just paw-fect', 3: 'A bit chonky' };
    const treatsMap = { 1: 'Never', 2: 'Sometimes', 3: 'Pretty often' };
    const fussyMap = { Yes: 'Very fussy', Between: 'A little fussy', No: 'Not fussy' };

    const totalMonths = (profile.years * 12) + profile.months;
    const lifeStage = totalMonths < 12 ? 'Puppy' : totalMonths > 84 ? 'Senior' : 'Adult';

    const klaviyoProfile = {
      dog_name: profile.name,
      dog_gender: profile.gender,
      dog_spayed_neutered: profile.neutered,
      dog_breed: profile.breed,
      dog_life_stage: lifeStage,
      dog_weight_kg: profile.weight,
      dog_bodytype: bodyTypeMap[profile.weightgroup],
      dog_activity_level: activityMap[profile.activity],
      dog_treats_per_day: treatsMap[profile.treats],
      dog_fussy_level: fussyMap[profile.fussy_dog],
      dog_allergies_dislikes: profile.allergies,
      dog_current_diet: profile.food,
      dog_health_issues: profile.health_issues,
      dog_treat_preference: profile.prefered_treat,
      dog_current_food_brand: profile.food_brand,
      dog_thrive_goal: profile.thrive,
      dog_has_conditions: profile.hasconditions,
      dog_conditions: profile.conditions.join(', '),
      dog_food_issues: profile.food_issues,
      dog_other_food_issue: profile.other_food_issue,
      _quiz_version: 'wizard-v2',
    };

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=R6vSRF', {
      method: 'POST',
      headers: { revision: '2023-06-15', 'content-type': 'application/json' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            list_id: 'XWKRtA',
            custom_source: 'Wizard',
            email: userEmail,
            properties: { hasPuppy: this.isPuppy, ...klaviyoProfile },
          },
        },
      }),
    })
      .then(r => { if (!r.ok) return r.json().then(err => Promise.reject(err)); })
      .then(() => fetch('https://a.klaviyo.com/client/events/?company_id=R6vSRF', {
        method: 'POST',
        headers: { revision: '2023-06-15', 'content-type': 'application/json' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: klaviyoProfile,
              metric: { name: 'Completed Dog Quiz' },
              profile: { email: userEmail },
            },
          },
        }),
      }))
      .catch(err => console.error('Klaviyo API Error:', err));
  }

  // ─── Persistence ──────────────────────────────────────────────────────────────

  saveProgress(step) {
    const existing = JSON.parse(localStorage.getItem('quizAnswers-v2') || '{}');
    const newData = this._getInput();
    newData.step = step;
    newData.age_group = document.querySelector('input[name="age_group"]:checked')?.value || '';
    newData.discount_code = this.discountCode || '';
    newData.customer_email = document.querySelector('input[name="owner_email"]')?.value || '';
    newData.accepts_newsletter = document.querySelector('input[name="mc_signup"]')?.checked || false;
    localStorage.setItem('quizAnswers-v2', JSON.stringify({ ...existing, ...newData }));
  }

  _prepopulate(rawData) {
    if (!rawData) return;
    const data = JSON.parse(rawData);

    // Gender
    if (data.gender) {
      const label = document.getElementById('neutered-spayed');
      if (label) {
        const old = label.innerHTML;
        label.innerHTML = data.gender === 'Boy'
          ? old.replace('Spayed', 'Neutered')
          : old.replace('Neutered', 'Spayed');
      }
      const genderInput = document.querySelector(`input[name="gender"][value="${data.gender.charAt(0).toUpperCase() + data.gender.slice(1)}"]`);
      if (genderInput) {
        genderInput.checked = true;
        // Setting .checked doesn't fire change, so update the gender spans
        // directly. Document-scoped for spans on the plan / add-ons pages.
        document.querySelectorAll('.gender-update').forEach(el => {
          el.textContent = data.gender === 'Boy' ? 'he' : 'she';
        });
        document.querySelectorAll('.gender-update-possessive').forEach(el => {
          el.textContent = data.gender === 'Boy' ? 'his' : 'her';
        });
      }
    }

    // Name
    if (data.name) {
      const nameInput = document.querySelector('input[data-order="name"]');
      if (nameInput) { nameInput.value = data.name; updateName(data.name); }
    }

    // Age
    const months = data.months || 0;
    const years = data.years || 0;
    const monthsInput = document.querySelector('input[name="months"]');
    const yearsInput = document.querySelector('input[name="years"]');
    // Only populate when there's a real value, so empty fields keep their placeholder
    if (monthsInput) monthsInput.value = months || '';
    if (yearsInput) yearsInput.value = years || '';

    const totalMonths = (years * 12) + months;
    // The number drives the group: derive it from the saved age when an age was
    // entered; otherwise fall back to the group the user explicitly selected.
    const restoredGroup = (years || months)
      ? this._ageGroupForMonths(totalMonths)
      : data.age_group;
    if (restoredGroup) {
      const groupRadio = document.querySelector(`input[name="age_group"][value="${restoredGroup}"]`);
      if (groupRadio) groupRadio.checked = true;
    }

    // Reveal + configure the age inputs for the restored group selection
    document.querySelector('input[name="age_group"]:checked')?.dispatchEvent(new Event('change'));

    // Breed
    if (data.breed) {
      const breedInput = document.querySelector('input[name="breed__autocomplete"]');
      if (data.breed === 'Pawfect mix') {
        const crossbreedRadio = document.getElementById('crossbreed');
        if (crossbreedRadio) crossbreedRadio.checked = true;
        if (breedInput) {
          breedInput.value = 'Pawfect mix';
          // Match the "Mix" selection: no suggestions dropdown/chevron, but still editable
          breedInput.setAttribute('data-autocomplete-off', '');
        }
      } else {
        const purebredRadio = document.getElementById('purebred');
        if (purebredRadio) purebredRadio.checked = true;
        if (breedInput) {
          breedInput.value = data.breed;
          breedInput.removeAttribute('data-autocomplete-off');
        }
      }
    }

    // Weight — only override the 10kg default when a real saved weight exists
    const savedWeight = parseFloat(data.weight);
    if (!isNaN(savedWeight) && savedWeight >= 1) {
      const weightText = document.getElementById('weightTextInput');
      const weightRange = document.getElementById('weightRangeInput');
      if (weightText) weightText.value = savedWeight;
      if (weightRange) weightRange.value = savedWeight;
      // Refresh the slider fill via the already-registered change handler
      weightText?.dispatchEvent(new Event('change'));
    }

    // Neutered
    if (data.neutered) {
      const neuteredInput = document.querySelector(`input[name="neutered"][value="${data.neutered}"]`);
      if (neuteredInput) neuteredInput.checked = true;
    }

    // Body type
    if (data.weightgroup) {
      const wgInput = document.querySelector(`input[name="weightgroup"][value="${data.weightgroup}"]`);
      if (wgInput) wgInput.checked = true;
    }

    // Activity (fix: was reading weightgroup — now reads activity)
    if (data.activity) {
      const actInput = document.querySelector(`input[name="activity"][value="${data.activity}"]`);
      if (actInput) actInput.checked = true;
    }

    // Checkboxes
    const checkAll = (name, values) => {
      if (!values?.length) return;
      values.forEach(val => {
        const cap = val.charAt(0).toUpperCase() + val.slice(1);
        const el = document.querySelector(`input[name="${name}"][type="checkbox"][value="${cap}"]`)
          || document.querySelector(`input[name="${name}"][type="checkbox"][value="${val}"]`);
        if (el) el.checked = true;
      });
    };

    checkAll('allergies', data.allergies);
    checkAll('health_issues', data.health_issues);
    checkAll('prefered_treat', data.prefered_treat);

    // Step 13 current diet is a single-select radio; data.food stores its value
    // lowercased, so match case-insensitively against the radio values.
    const foodVal = Array.isArray(data.food) ? data.food[0] : data.food;
    if (foodVal) {
      const foodRadio = Array.from(document.querySelectorAll('input[name="current__food"]'))
        .find(el => el.value.toLowerCase() === String(foodVal).toLowerCase());
      if (foodRadio) foodRadio.checked = true;
    }

    // Current diet brand (optional — never blocks the quiz)
    if (data.food_brand) {
      const brandInput = document.querySelector('input[name="current_food_brand"]');
      if (brandInput) brandInput.value = data.food_brand;
    }

    // Step 12 "thrive" — restore the radio and re-render its fact callout
    if (data.thrive) {
      const thriveRadio = document.querySelector(`input[name="thrive"][value="${data.thrive}"]`);
      if (thriveRadio) {
        thriveRadio.checked = true;
        thriveRadio.dispatchEvent(new Event('change'));
      }
    }

    // Medical conditions — restore the None/Some radio and any checked conditions
    if (data.hasconditions) {
      const conditionsRadio = document.querySelector(`input[name="hasconditions"][value="${data.hasconditions}"]`);
      if (conditionsRadio) conditionsRadio.checked = true;
      (data.conditions || []).forEach(val => {
        const cb = document.querySelector(`input[name="condition"][value="${val}"]`);
        if (cb) cb.checked = true;
      });
      // Fire change so the conditions list shows/hides to match the restored choice
      conditionsRadio?.dispatchEvent(new Event('change'));
    }

    // Step 14 — what's not working (radio) + optional "something else" text
    if (data.food_issues) {
      const foodIssueRadio = document.querySelector(`input[name="food_issues"][value="${data.food_issues}"]`);
      if (foodIssueRadio) foodIssueRadio.checked = true;
    }
    const otherFoodIssue = document.querySelector('textarea[name="other_food_issue"]');
    if (otherFoodIssue) {
      // Only keep the "something else" text when "Other" is the selected issue
      otherFoodIssue.value = data.food_issues === 'Other' ? (data.other_food_issue || '') : '';
    }

    if (data.treats) {
      const treatsInput = document.querySelector(`input[name="treats"][value="${data.treats}"]`);
      if (treatsInput) treatsInput.checked = true;
    }
    if (data.fussy_dog) {
      const fussyInput = document.querySelector(`input[name="fussy_dog"][value="${data.fussy_dog}"]`);
      if (fussyInput) fussyInput.checked = true;
    }

    // Resume step — render the saved step directly so the user lands on it
    // (previously this clicked "start", which validated & advanced one step and
    // left the user stranded on step 1 when the saved step had missing data).
    if (data.step) {
      // saveProgress records the step the user just COMPLETED, so resume on the
      // next question rather than re-showing an already-answered one.
      // If they already reached the final step, land there (the summary); otherwise
      // advance one step but never past the last question.
      this.step = data.step >= this.lastStep
        ? this.lastStep
        : Math.min(data.step + 1, this.lastStep - 1);

      // Mark earlier steps complete, the current step active
      document.querySelectorAll('.progress__step').forEach(el => {
        const stepNum = Number(el.dataset.step);
        el.classList.remove('is-active', 'is-complete');
        if (stepNum < this.step) el.classList.add('is-complete');
        else if (stepNum === this.step) el.classList.add('is-active');
      });

      // Update header chrome to match the landed step
      const counter = document.querySelector('.progress__counter span');
      if (counter) counter.innerHTML = this.step - 1;

      const progressBar = this.querySelector('.quiz-header__progress-bar');
      if (progressBar) progressBar.style.width = `${(this.step / this.lastStep) * 100}%`;

      this.querySelector('.quiz-header__back')?.classList.toggle('disabled', this.step === 1);
      this.nextButton?.classList.remove('disabled');

      // Render the saved step's section and sync the checkout flow
      handleSections(this.step);
      this.dataset.current = this.step;
      this._checkoutFlow(this.step);
      // If resuming onto the final step, pick the fitting Trustpilot review
      if (this.step === this.lastStep) this._updateTrustpilotQuote();
      scrollToTop();
    }

    // Discount code — re-apply the saved code automatically
    if (data.discount_code) {
      const discountInput = document.querySelector('input[name="discount"]');
      if (discountInput) discountInput.value = data.discount_code;
      this._handleCoupon(data.discount_code);
    }

    // Email
    if (data.customer_email) {
      const emailInput = document.querySelector('input[name="owner_email"]');
      if (emailInput) emailInput.value = data.customer_email;
    }

    requestAnimationFrame(() => {
      this._calculateMealPlan(JSON.stringify(this._getInput()));

      // Resumed straight onto the email step (e.g. a refresh) — kick off the
      // AI plan content fetch now, same as the normal step-18 "Next" flow, so
      // it isn't left to start cold on final submit. this._planReadyPromise
      // stands for "meal plan calc is ready" (used to gate the loader
      // overlay) — the AI fetch is chained off it but NOT folded into it, so
      // a slow AI response never blocks the overlay.
      if (this.step === this.lastStep) {
        this._planReadyPromise = Promise.resolve();
        this._planReadyPromise.then(() => this._loadPlanContent());
        setCurrentFoodImage();
      }
    });
  }

  // ─── Age Toggle ───────────────────────────────────────────────────────────────

  // Map a total age in months to its age group, using the same thresholds
  // as the calorie math: <12mo Puppy, 12–84mo Adult, >84mo Senior.
  _ageGroupForMonths(totalMonths) {
    if (totalMonths < 12) return 'Puppy';
    if (totalMonths > 84) return 'Senior';
    return 'Adult';
  }

  // Step 6 founder note — swaps the message to match the selected age group
  // and injects the dog's name.
  _updateFounderNote(group) {
    const el = this.querySelector('[data-founder-message]');
    if (!el) return;

    const name = (this._qiv('dog_name') || this.dogName || 'your pup').trim();
    const safe = name.replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    const possessive = `${safe}${/s$/i.test(name) ? '’' : '’s'}`;

    const messages = {
      Puppy: `How exciting — thousands of pups are already on Marleybones! ${safe} is in great hands.`,
      Adult: `The best years are ahead. We’ll make sure ${possessive} nutrition keeps up with them.`,
      Senior: `Senior dogs deserve just as much love at mealtimes. We’ll build ${possessive} plan around their needs.`,
    };

    if (messages[group]) {
      el.innerHTML = messages[group];
      el.closest('[data-founder-note]').style.display = 'block';
    }
  }

  // Step 19 Trustpilot quote — pick a review based on the user's answers.
  // Rules are evaluated in order; the first whose match() returns true wins,
  // falling back to DEFAULT. Fill in real quotes + logic below.
  _updateTrustpilotQuote() {
    // Quote appears in the in-quiz Trustpilot block and again in the plan
    // overview (outside <quiz-wizard>), so query the whole document.
    const quoteEls = document.querySelectorAll('[data-trustpilot-quote]');
    const authorEls = document.querySelectorAll('[data-trustpilot-author]');
    if (!quoteEls.length) return;

    // Quote is chosen from the step-12 "thrive" answer (with a body-type split
    // for "Healthy weight"). thrive isn't in _getInput, so read it directly.
    const thrive = this.querySelector('input[name="thrive"]:checked')?.value;
    const weightgroup = parseInt(this.querySelector('input[name="weightgroup"]:checked')?.value); // 1 skinny, 2 pawfect, 3 chonky

    const DEFAULT = {
      quote: `"My dog wouldn't touch the old brand's meals. Marleybones changed everything—now she eagerly waits for every meal!"`,
      author: `Emily T`,
    };

    const QUOTES = {
      'Better digestion': {
        quote: `"She had always had a problem with soft poos no matter what we tried and when we saw some of the Marleybones reviews, thought it was worth giving it a go. It wasn't long before we saw a lot of improvement. She is excited at mealtimes and we love the ease of use!"`,
        author: `Marina Steel`,
      },
      'Less fussy': {
        quote: `"Amazing service and amazing food - our very fussy cocker spaniel loves it."`,
        author: `Kerry Watts`,
      },
      'Healthier coat': {
        quote: `"Since changing my French Bulldog to Marleybones she has enjoyed every bowl. Her coat and skin seem much better - brilliant food and great customer service."`,
        author: `Tracey Cutler`,
      },
      'Healthier life': {
        quote: `"Marleybones is exceptional dog food. My dog LOVES it and is in the best shape of his life since starting his new dog food journey - he has more energy, a soft and silky coat, and is loving life."`,
        author: `Kate Woolford`,
      },
    };

    // "Healthy weight" splits by body type: chonky (3) gets a different review.
    const HEALTHY_WEIGHT_CHONKY = {
      quote: `"We followed the feeding guide on the box and she is now up to and maintaining a healthy weight, her coat is now full and glossy and she is full of energy. The vet is very pleased with her progress."`,
      author: `SC`,
    };
    const HEALTHY_WEIGHT_DEFAULT = {
      quote: `"My dog absolutely loves this food. His weight had rapidly dropped, however after swapping him onto Marleybones his weight is absolutely perfect again."`,
      author: `Kayla Ruddy`,
    };

    let chosen = DEFAULT;
    if (thrive === 'Healthy weight') {
      chosen = weightgroup === 3 ? HEALTHY_WEIGHT_CHONKY : HEALTHY_WEIGHT_DEFAULT;
    } else if (QUOTES[thrive]) {
      chosen = QUOTES[thrive];
    }

    quoteEls.forEach(el => { el.innerHTML = chosen.quote; });
    authorEls.forEach(el => { el.innerHTML = `<strong>${chosen.author}</strong>`; });
  }

  // Confetti burst on age-group selection (window.confetti loaded via CDN)
  _celebrate() {
    if (typeof window.confetti !== 'function') return;

    window.confetti({
      spread: 360,
      ticks: 200,
      gravity: 1,
      decay: .94,
      startVelocity: 30,
      particleCount: 100,
      scalar: 3,
      shapes: ["image"],
      shapeOptions: {
        image: [{
          src: "https://cdn.shopify.com/s/files/1/0784/5448/9374/files/confetti-1.svg?v=1783026389",
          width: 32,
          height: 32
        },
        {
          src: "https://cdn.shopify.com/s/files/1/0784/5448/9374/files/confetti-2.svg?v=1783026389",
          width: 32,
          height: 32
        },
        {
          src: "https://cdn.shopify.com/s/files/1/0784/5448/9374/files/confetti-3.svg?v=1783026388",
          width: 32,
          height: 32
        },
        {
          src: "https://cdn.shopify.com/s/files/1/0784/5448/9374/files/confetti-4.svg?v=1783026388",
          width: 32,
          height: 32
        },
        {
          src: "https://cdn.shopify.com/s/files/1/0784/5448/9374/files/confetti-5.svg?v=1783026389",
          width: 32,
          height: 32
        }]
      }
    });
  }

  _initAgeToggle() {
    const ageGroupRadios = this.querySelectorAll('input[name="age_group"]');
    const inputYears = this.querySelector('input[name="years"]');
    const inputMonths = this.querySelector('input[name="months"]');
    const inputYearsContainer = this.querySelector('#input_age_years');
    const ageInputSwitch = this.querySelector('#input_age');

    // Show/hide the year & month fields for a group, without clearing their values.
    const applyFieldVisibility = (selected) => {
      if (!selected) {
        if (ageInputSwitch) ageInputSwitch.style.display = 'none';
        return;
      }
      if (ageInputSwitch) ageInputSwitch.style.display = 'flex';

      if (selected === 'Puppy') {
        if (inputYearsContainer) inputYearsContainer.style.display = 'none';
        if (inputMonths?.parentElement) inputMonths.parentElement.style.display = 'block';
        inputMonths?.setAttribute('required', 'required');
        inputYears?.removeAttribute('required');
      } else {
        if (inputYearsContainer) inputYearsContainer.style.display = 'block';
        if (inputMonths?.parentElement) inputMonths.parentElement.style.display = 'block';
        inputYears?.setAttribute('required', 'required');
        inputMonths?.removeAttribute('required');
      }
    };

    // When the user PICKS a group manually, reset the age fields for that group.
    const onGroupChange = (event) => {
      const selected = this.querySelector('input[name="age_group"]:checked')?.value;
      applyFieldVisibility(selected);
      this._updateFounderNote(selected);
      // Only celebrate on a genuine user selection — not programmatic change
      // events dispatched during prepopulate/resume (event.isTrusted === false).
      if (event?.isTrusted) this._celebrate();
      // Puppy uses months only — clear any leftover years value.
      if (selected === 'Puppy' && inputYears) {
        inputYears.value = '';
        inputYears.dispatchEvent(new Event('change'));
      }
    };

    // When the user TYPES an age, derive the group from the number (number is the
    // source of truth) and update the selected radio + field visibility to match.
    const syncGroupFromAge = () => {
      const totalMonths = (Number(inputYears?.value) * 12) + Number(inputMonths?.value || 0);
      // Nothing entered yet — leave the current selection alone.
      if (!inputYears?.value && !inputMonths?.value) return;

      const group = this._ageGroupForMonths(totalMonths);
      const groupRadio = this.querySelector(`input[name="age_group"][value="${group}"]`);
      if (groupRadio && !groupRadio.checked) {
        groupRadio.checked = true;
        applyFieldVisibility(group);
        this._updateFounderNote(group);
        this.saveProgress(this.step);
      }
    };

    ageGroupRadios.forEach(radio => radio.addEventListener('change', onGroupChange));
    inputYears?.addEventListener('input', syncGroupFromAge);
    inputMonths?.addEventListener('input', syncGroupFromAge);

    applyFieldVisibility(this.querySelector('input[name="age_group"]:checked')?.value);
  }

  // ─── Breed Logic ─────────────────────────────────────────────────────────────

  _initBreedLogic() {
    const purebredRadio = this.querySelector('#purebred');
    const crossbreedRadio = this.querySelector('#crossbreed');
    const breedInput = this.querySelector('#breed__autocomplete');

    const handleRadioChange = () => {
      if (purebredRadio?.checked) {
        breedInput?.setAttribute('required', 'required');
        // Re-enable the breed suggestions dropdown + chevron
        breedInput?.removeAttribute('data-autocomplete-off');
        if (breedInput) breedInput.value = '';
      } else if (crossbreedRadio?.checked) {
        breedInput?.removeAttribute('required');
        if (breedInput) breedInput.value = 'Pawfect mix';
        // Turn off the suggestions dropdown + chevron, but keep the field editable
        breedInput?.setAttribute('data-autocomplete-off', '');
        // Close the dropdown if it happens to be open
        document.querySelectorAll('.autocomplete-items').forEach(el => el.remove());
      }
    };

    purebredRadio?.addEventListener('change', handleRadioChange);
    crossbreedRadio?.addEventListener('change', handleRadioChange);

    // Default to "Breed" (purebred) unless a selection already exists (e.g. resumed quiz)
    if (purebredRadio && !purebredRadio.checked && !crossbreedRadio?.checked) {
      purebredRadio.checked = true;
    }

    handleRadioChange();
  }

  // ─── Checkbox Exclusives ─────────────────────────────────────────────────────

  _initCheckboxExclusives() {
    const makeExclusive = (name, noneValue) => {
      const inputs = this.querySelectorAll(`input[name="${name}"]`);
      inputs.forEach(input => {
        input.addEventListener('click', () => {
          if (input.value === noneValue) {
            if (input.checked) {
              inputs.forEach(other => { if (other.value !== noneValue) other.checked = false; });
            }
          } else {
            const noneInput = this.querySelector(`input[name="${name}"][value="${noneValue}"]`);
            if (noneInput) noneInput.checked = false;
          }
        });
      });
    };

    makeExclusive('allergies', 'Nothing');
    makeExclusive('health_issues', 'No health issues');
    makeExclusive('prefered_treat', 'none');
  }

  // ─── Conditions Toggle ────────────────────────────────────────────────────────

  _initConditionsToggle() {
    const block = this.querySelector('[data-has-conditions]');
    if (!block) return;

    const radios = this.querySelectorAll('input[name="hasconditions"]');

    const toggle = () => {
      const selected = this.querySelector('input[name="hasconditions"]:checked')?.value;
      if (selected === 'Some') {
        block.style.display = 'block';
      } else {
        block.style.display = 'none';
        // Clear any condition selections when they're no longer applicable
        block.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => { cb.checked = false; });
      }
    };

    radios.forEach(radio => radio.addEventListener('change', toggle));
    toggle();
  }

  // ─── Thrive Callout ───────────────────────────────────────────────────────────

  _initThriveCallout() {
    const callout = this.querySelector('[data-thrive-callout]');
    const headlineEl = this.querySelector('[data-thrive-headline]');
    const bodyEl = this.querySelector('[data-thrive-body]');
    if (!callout || !headlineEl || !bodyEl) return;

    // Copy per "thrive" selection. {name}/{name's}/{they} tokens are filled at render.
    const content = {
      'Better digestion': {
        headline: '71% of customers…',
        body: "report better digestion and stool quality after switching — {name's} gut health goal is front and centre in {their} plan.",
      },
      'Less fussy': {
        headline: '9 in 10 former fussy dogs…',
        body: 'now enjoy mealtimes with Marleybones — so we’re confident {name} will too.',
      },
      'Healthy weight': {
        headline: '76% of customers…',
        body: "reported improvements to their dog's weight after switching — nutritionally balanced meals to help support {name} reach a healthy weight.",
      },
      'Healthier coat': {
        headline: '1 in 2 customers…',
        body: "have reported improvements in their dog's skin & coat since switching to Marleybones.",
      },
      'Healthier life': {
        headline: '98% of owners…',
        body: "are glad they switched to Marleybones — {name's} health starts with what's in their bowl.",
      },
    };

    const render = () => {
      const selected = this.querySelector('input[name="thrive"]:checked')?.value;
      const copy = selected && content[selected];
      if (!copy) {
        callout.style.display = 'none';
        return;
      }

      const name = (this._qiv('dog_name') || this.dogName || 'your pup').trim();
      const safe = name.replace(/[&<>"']/g, c => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
      const possessive = `${safe}${/s$/i.test(name) ? '’' : '’s'}`;
      const gender = this.querySelector('input[name="gender"]:checked')?.value;
      const their = gender === 'Boy' ? 'his' : gender === 'Girl' ? 'her' : 'their';

      const fill = (str) => str
        .replace(/\{name's\}/g, possessive)
        .replace(/\{name\}/g, safe)
        .replace(/\{their\}/g, their);

      headlineEl.textContent = copy.headline;
      bodyEl.innerHTML = fill(copy.body);
      callout.style.display = 'block';
    };

    this.querySelectorAll('input[name="thrive"]').forEach(radio =>
      radio.addEventListener('change', render)
    );
    render();
  }

  // ─── Autocomplete ─────────────────────────────────────────────────────────────

  _initAutocomplete() {
    const sources = {
      dietBrands: window.dietBrandsData || '',
      default: window.breedsData || '',
    };
    this.querySelectorAll('.autocomplete').forEach(input => {
      const key = input.dataset.autocompleteSource || 'default';
      const list = (sources[key] || '').split('\n');
      this._autocomplete(input, list);
    });

    // Close any open list on outside clicks — registered once for all fields.
    // (Clicking an autocomplete input or an item is handled by their own listeners.)
    document.addEventListener('click', (e) => {
      if (e.target.classList?.contains('autocomplete') || e.target.closest('.autocomplete-items')) return;
      document.querySelectorAll('.autocomplete-items').forEach(el => el.remove());
    });
  }

  _autocomplete(inp, arr) {
    let currentFocus = -1;

    // Drop empty entries (e.g. trailing newline from the CSV split)
    const breeds = arr.map(b => b.trim()).filter(Boolean);

    const closeAllLists = (except) => {
      document.querySelectorAll('.autocomplete-items').forEach(el => {
        if (el !== except && el !== inp) el.parentNode.removeChild(el);
      });
    };

    // Build the dropdown. With no filter value, shows every breed (scrollable);
    // with a value, shows only matches with the match highlighted.
    const renderList = (val) => {
      closeAllLists();
      currentFocus = -1;

      // Suggestions disabled (e.g. "Mix" selected) — field stays editable, just no dropdown
      if (inp.hasAttribute('data-autocomplete-off')) return;

      const filter = (val || '').toLowerCase().trim();

      const list = document.createElement('DIV');
      list.setAttribute('id', inp.id + 'autocomplete-list');
      list.setAttribute('class', 'autocomplete-items');
      inp.parentNode.appendChild(list);

      for (const breed of breeds) {
        const lower = breed.toLowerCase();
        if (filter && lower.indexOf(filter) === -1) continue;

        const item = document.createElement('DIV');
        item.innerHTML = filter
          ? lower.replace(filter, `<strong>${filter}</strong>`)
          : lower;
        item.innerHTML += `<input type='hidden' value='${breed}'>`;
        item.addEventListener('click', function () {
          inp.value = this.getElementsByTagName('input')[0].value;
          inp.dispatchEvent(new Event('change'));
          closeAllLists();
        });
        list.appendChild(item);
      }
    };

    // Focus → show the full list so the user can just scroll and click
    inp.addEventListener('focus', function () {
      renderList(this.value);
    });

    // Typing → filter (and still show the full list when the field is cleared)
    inp.addEventListener('input', function () {
      renderList(this.value);
    });

    inp.addEventListener('keydown', function (e) {
      let x = document.getElementById(this.id + 'autocomplete-list');
      if (x) x = x.getElementsByTagName('div');

      if (e.keyCode === 40) {
        currentFocus++;
        addActive(x);
      } else if (e.keyCode === 38) {
        currentFocus--;
        addActive(x);
      } else if (e.keyCode === 13) {
        e.preventDefault();
        if (currentFocus > -1 && x) x[currentFocus]?.click();
      }
    });

    const addActive = (x) => {
      if (!x) return;
      Array.from(x).forEach(el => el.classList.remove('autocomplete-active'));
      if (currentFocus >= x.length) currentFocus = 0;
      if (currentFocus < 0) currentFocus = x.length - 1;
      x[currentFocus]?.classList.add('autocomplete-active');
    };
    // Outside-click closing is handled once in _initAutocomplete for all fields.
  }

  // ─── Resume Flow ─────────────────────────────────────────────────────────────

  _initResumeFlow() {
    const previousQuizData = localStorage.getItem('quizAnswers-v2');
    if (previousQuizData) {
      const saved = JSON.parse(previousQuizData);
      if (saved.name) updateName(saved.name);
      this.querySelector('.quiz-started-overlay')?.classList.add('show-modal');
    }
  }

  // ─── AI Plan Content ──────────────────────────────────────────────────────────

  // Assemble the object sent to the plan-content endpoint: the raw quiz answers
  // plus the product NAMES that were recommended in the plan. Must run after
  // _calculateMealPlan (which populates starterBox / starterIndex).
  _buildQuizPayload() {
    // Recommended product titles per group. starterBox holds recommended
    // quantities (keyed by protein/issue); starterIndex maps each key to its
    // product { title, ... }. recommendedAddons is the original recommendation
    // for treats/supplements (before any add-ons-step edits).
    const namesFor = (group, keys) =>
      [...keys]
        .filter(key => this.starterIndex?.[group]?.[key])
        .map(key => this.starterIndex[group][key].title);

    const mealKeys = Object.keys(this.starterBox?.meals || {})
      .filter(key => this.starterBox.meals[key] > 0);

    // Slider answers are stored as raw 1–3 values; send their human-readable
    // labels (matching setTreatsFromValue / setWeightFromValue / setActivity-
    // FromValue in range.js) so the plan-content endpoint gets readable text.
    const LABELS = {
      treats: { 1: 'Never', 2: 'Sometimes', 3: 'Pretty often' },
      weightgroup: { 1: 'A bit skinny', 2: 'Just pawfect', 3: 'A bit chubby' },
      activity: { 1: 'Pretty snoozy', 2: 'Active', 3: 'An explosion of energy' },
    };

    const answers = this._getInput();
    for (const field of ['treats', 'weightgroup', 'activity']) {
      answers[field] = LABELS[field][answers[field]] ?? answers[field];
    }

    // Client request: never send health conditions (suitable or unsuitable)
    // to the plan-content endpoint. _getInput() keeps them for the rest of
    // the quiz (subscription data, restore, etc.).
    delete answers.conditions;
    delete answers.hasconditions;

    return {
      answers,
      dogName: this.dogName,
      recommendation: {
        meals: namesFor('meals', mealKeys),
        treats: namesFor('treats', this.recommendedAddons?.treats || []),
        supplements: namesFor('supplements', this.recommendedAddons?.supplements || []),
      },
    };
  }

  // TESTING ONLY — dump the _buildQuizPayload JSON into the debug <code> block
  // on the email step. Recommendation fields are empty until the plan is
  // calculated (on quiz completion).
  _renderPayloadDebug() {
    const el = document.querySelector('[data-quiz-payload-debug] code');
    if (!el) return;
    try {
      el.textContent = JSON.stringify(this._buildQuizPayload(), null, 2);
    } catch (err) {
      el.textContent = `payload error: ${err.message}`;
    }
  }

  // PLACEHOLDER — POST the quiz payload to the plan-content endpoint and return
  // the parsed response. Swap PLAN_CONTENT_ENDPOINT for the real Vercel URL.
  async _fetchPlanContent(payload) {
    const PLAN_CONTENT_ENDPOINT = 'https://marleybones-quiz-ai.vercel.app/api/quiz';

    const startedAt = performance.now();
    try {
      const res = await fetch(PLAN_CONTENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Plan content request failed: ${res.status}`);
      const data = await res.json();
      console.log(`⏱️ [wizard-v2] AI plan content responded in ${Math.round(performance.now() - startedAt)}ms`);
      return data;
    } catch (err) {
      console.error(`Could not fetch AI plan content (after ${Math.round(performance.now() - startedAt)}ms):`, err);
      return null; // fall back to the static plan content already in the DOM
    }
  }

  // Apply the plan-content endpoint's response to the DOM. Leaves the static
  // placeholder markup in place for any field that's missing or on a
  // null/failed response (see _fetchPlanContent). customer_frustration_strapline
  // is intentionally unused for now.
  _applyPlanContent(response) {
    if (!response) return;

    console.log("ai 2 response", response);

    const reasonEl = document.querySelector('[data-plan-reason]');
    if (reasonEl && response.why_this_plan) reasonEl.textContent = response.why_this_plan;

    const whyTheseEl = document.querySelector('[data-why-these]');
    if (whyTheseEl && response.why_these) whyTheseEl.textContent = response.why_these;

    const customerFrustrationEl = document.querySelector('[data-customer-frustration]');
    console.log('customerFrustrationEl', customerFrustrationEl);
    if (customerFrustrationEl && response.customer_frustration_strapline) customerFrustrationEl.textContent = response.customer_frustration_strapline;

    if (response.three_priorities?.length) this._renderPriorities(response.three_priorities);
  }

  async _loadPlanContent() {
    const payload = this._buildQuizPayload();
    console.log('📤 [wizard-v2] quiz payload for plan content:', payload);
    this._renderPayloadDebug(); // TESTING ONLY — refresh with recommendation data
    const response = await this._fetchPlanContent(payload);
    this._applyPlanContent(response);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  _checkoutFlow(step) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'checkout', ecommerce: { checkout: { actionField: { step } } } });
  }

  _emptyCartOnStart() {
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }).catch(err => console.error('Error clearing cart:', err));
  }

  _puppyModalHelper(months) {
    const limitModal = this.querySelector('.limit-modal');
    if (!limitModal) return;
    if (months > 180) {
      const top = (window.pageYOffset || document.documentElement.scrollTop);
      limitModal.style.marginTop = top + 'px';
      limitModal.style.display = 'inline-block';
    }
  }

  _getCookie(name) {
    const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return match ? match.pop() : '';
  }

}

customElements.define('quiz-wizard', QuizWizard);
